import React, { useMemo, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const SharedData = ({ segments, events, projectConfig }) => {
  const threshold = projectConfig?.iri_threshold_type_a || 160;

  // Collapse state for each table
  const [segmentsOpen, setSegmentsOpen] = useState(true);
  const [lrOpen, setLrOpen] = useState(false);
  const [straightedgeOpen, setStraightedgeOpen] = useState(false);

  // Filter segments
  const validSegments = useMemo(() => 
    segments?.filter(s => !s.exception && s.iri_avg_in_per_mi > 0) || [],
    [segments]
  );

  // Filter events by type
  const lrEvents = useMemo(() => 
    events?.filter(e => e.event_type === 'LR') || [],
    [events]
  );

  const straightedgeEvents = useMemo(() => 
    events?.filter(e => e.event_type === 'Bump' || e.event_type === 'Dip') || [],
    [events]
  );

  // Format station from feet (e.g., 40244160 → 402441+60)
  const formatStation = (ft) => {
    if (ft == null) return '—';
    const station = Math.floor(ft / 100);
    const offset = Math.round(ft % 100);
    return `${station}+${offset.toString().padStart(2, '0')}`;
  };

  // Calculate start distance relative to first segment
  const baseStation = validSegments[0]?.from_station_ft || 0;

  return (
    <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 sm:py-6 space-y-0 sm:space-y-4">
      {/* Fixed Interval Segments */}
      <CollapsibleTable 
        title="Fixed Interval" 
        count={validSegments.length}
        isOpen={segmentsOpen}
        onToggle={() => setSegmentsOpen(!segmentsOpen)}
      >
        <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <Th>Start Dist</Th>
                <Th>Stop Dist</Th>
                <Th>Start Sta</Th>
                <Th>Stop Sta</Th>
                <Th>L IRI</Th>
                <Th>R IRI</Th>
                <Th>MRI</Th>
                <Th>ALRs</Th>
              </tr>
            </thead>
            <tbody>
              {validSegments.map((seg) => {
                const startDist = seg.from_station_ft - baseStation;
                const stopDist = seg.to_station_ft - baseStation;
                const alrCount = seg.localized_roughness_count || 0;
                
                return (
                  <tr key={seg.segment_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <Td>{startDist.toFixed(1)}</Td>
                    <Td>{stopDist.toFixed(1)}</Td>
                    <Td mono>{formatStation(seg.from_station_ft)}</Td>
                    <Td mono>{formatStation(seg.to_station_ft)}</Td>
                    <Td>{seg.iri_left_in_per_mi?.toFixed(1)}</Td>
                    <Td>{seg.iri_right_in_per_mi?.toFixed(1)}</Td>
                    <Td>{seg.iri_avg_in_per_mi?.toFixed(1)}</Td>
                    <Td>{alrCount}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CollapsibleTable>

      {/* Localized Roughness */}
      <CollapsibleTable 
        title="Localized Roughness" 
        count={lrEvents.length}
        isOpen={lrOpen}
        onToggle={() => setLrOpen(!lrOpen)}
      >
        {lrEvents.length === 0 ? (
          <EmptyState message="No localized roughness events detected" />
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <Th>Start Sta</Th>
                  <Th>Stop Sta</Th>
                  <Th>Peak Sta</Th>
                  <Th>Length</Th>
                  <Th>WP</Th>
                  <Th>Mag</Th>
                </tr>
              </thead>
              <tbody>
                {lrEvents.map((event) => {
                  const length = (event.to_station_ft - event.from_station_ft).toFixed(1);
                  
                  return (
                    <tr key={event.event_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <Td mono>{formatStation(event.from_station_ft)}</Td>
                      <Td mono>{formatStation(event.to_station_ft)}</Td>
                      <Td mono>{formatStation(event.peak_station_ft)}</Td>
                      <Td>{length}</Td>
                      <Td>{event.wheelpath}</Td>
                      <Td>{event.magnitude_in?.toFixed(1)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleTable>

      {/* Rolling Straightedge */}
      <CollapsibleTable 
        title="Rolling Straightedge" 
        count={straightedgeEvents.length}
        isOpen={straightedgeOpen}
        onToggle={() => setStraightedgeOpen(!straightedgeOpen)}
      >
        {straightedgeEvents.length === 0 ? (
          <EmptyState message="No bump or dip events detected" />
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <Th>Type</Th>
                  <Th>Start Sta</Th>
                  <Th>Stop Sta</Th>
                  <Th>Peak Sta</Th>
                  <Th>Length</Th>
                  <Th>WP</Th>
                  <Th>Mag</Th>
                </tr>
              </thead>
              <tbody>
                {straightedgeEvents.map((event) => {
                  const length = (event.to_station_ft - event.from_station_ft).toFixed(1);
                  
                  return (
                    <tr key={event.event_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <Td>{event.event_type}</Td>
                      <Td mono>{formatStation(event.from_station_ft)}</Td>
                      <Td mono>{formatStation(event.to_station_ft)}</Td>
                      <Td mono>{formatStation(event.peak_station_ft)}</Td>
                      <Td>{length}</Td>
                      <Td>{event.wheelpath}</Td>
                      <Td>{event.magnitude_in?.toFixed(2)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleTable>
    </div>
  );
};

// Collapsible table card wrapper
const CollapsibleTable = ({ title, count, isOpen, onToggle, children }) => (
  <div className="bg-white sm:border border-b border-gray-200 sm:rounded-sm">
    <button 
      onClick={onToggle}
      className="w-full px-3 sm:px-4 py-3 border-b border-gray-200 flex items-center justify-between hover:bg-gray-50 active:bg-gray-100 transition-colors"
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <ChevronDownIcon 
          className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} 
        />
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
      </div>
      {count !== undefined && (
        <span className="text-xs text-gray-500">{count} records</span>
      )}
    </button>
    {isOpen && children}
  </div>
);

// Table header cell
const Th = ({ children }) => (
  <th className="px-2 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50 sticky top-0 whitespace-nowrap">
    {children}
  </th>
);

// Table data cell
const Td = ({ children, mono }) => (
  <td className={`px-2 sm:px-4 py-2 text-left text-xs sm:text-sm text-gray-900 whitespace-nowrap ${mono ? 'font-mono' : ''}`}>
    {children}
  </td>
);

// Empty state
const EmptyState = ({ message }) => (
  <div className="px-4 py-8 text-center text-sm text-gray-500">
    {message}
  </div>
);

export default SharedData;